import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

type ApiErrorBody = {
    response?: {
        data?: {
            data?: unknown;
            errors?: unknown;
            message?: unknown;
        };
    };
};

function messageFromUnknown(val: unknown): string | null {
    if (typeof val === 'string' && val.trim()) return val;
    if (Array.isArray(val)) {
        const parts = val.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
        if (parts.length) return parts.join(', ');
    }
    return null;
}

/**
 * Reads field messages from API error bodies like:
 * `{ data: { department_name: "…", description: "…" } }` (Axios `error.response.data`).
 */
export function extractValidationFieldErrors(error: unknown): Record<string, string> | null {
    const responseData = (error as ApiErrorBody)?.response?.data;
    const data = responseData?.data;
    const errors = responseData?.errors;
    const source = (errors && typeof errors === 'object' && !Array.isArray(errors)) ? errors : data;
    if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
    const out: Record<string, string> = {};
    for (const [key, val] of Object.entries(source)) {
        const message = messageFromUnknown(val);
        if (message) out[key] = message;
    }
    return Object.keys(out).length > 0 ? out : null;
}

/** True when the error includes at least one field-level validation message. */
export function hasValidationFieldErrors(error: unknown): boolean {
    return extractValidationFieldErrors(error) !== null;
}

/** Maps server `data` keys to react-hook-form field errors. */
export function applyServerValidationErrors<T extends FieldValues>(
    error: unknown,
    setError: UseFormSetError<T>,
): boolean {
    return applyServerValidationErrorsWithFields(error, setError).length > 0;
}

/** Maps server validation fields to RHF errors and returns the mapped field names. */
export function applyServerValidationErrorsWithFields<T extends FieldValues>(
    error: unknown,
    setError: UseFormSetError<T>,
    fieldMap?: Partial<Record<string, Path<T>>>,
): Path<T>[] {
    const fields = extractValidationFieldErrors(error);
    if (!fields) return [];
    const appliedFields: Path<T>[] = [];
    for (const [key, message] of Object.entries(fields)) {
        const mappedKey = fieldMap?.[key] ?? (key as Path<T>);
        setError(mappedKey, { type: 'server', message });
        appliedFields.push(mappedKey);
    }
    return appliedFields;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
    const msg = (error as ApiErrorBody)?.response?.data?.message;
    const fromUnknown = messageFromUnknown(msg);
    return fromUnknown ?? fallback;
}
